import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useImportDivisionUsers } from '../../hooks/use-division-users';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import { DivisionUsersService } from '../../services/division-users-service';
import httpClient from '../../services/http-client';
import type { DivisionUserImportResult } from '../../types';

interface UserImportModalProps {
  divisionId: string;
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
}

const divisionUsersService = new DivisionUsersService(httpClient);

export function UserImportModal({
  divisionId,
  isOpen,
  onClose,
  onImportComplete,
}: UserImportModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [includeMatchIdentifiers, setIncludeMatchIdentifiers] = useState(false);
  const [importResult, setImportResult] = useState<DivisionUserImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useImportDivisionUsers(divisionId);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setImportResult(null);
    
    // Auto-detect format from file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'csv' || extension === 'json') {
      setFormat(extension);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv') || file.name.endsWith('.json')) {
        handleFileSelect(file);
      } else {
        alert('Please select a CSV or JSON file');
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    const template = divisionUsersService.generateImportTemplate(format);
    const blob = new Blob([template], { 
      type: format === 'csv' ? 'text/csv' : 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-import-template.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }

    try {
      const result = await importMutation.mutateAsync({
        file: selectedFile,
        options: {
          format,
          updateExisting,
          includeMatchIdentifiers,
        },
      });

      setImportResult(result);

      if (result.summary.errorCount === 0) {
        // All successful
        setTimeout(() => {
          onImportComplete?.();
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please check your file and try again.');
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResult(null);
    setUpdateExisting(false);
    setIncludeMatchIdentifiers(false);
    onClose();
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  const handleDownloadErrors = () => {
    if (!importResult || importResult.errors.length === 0) return;

    const errorsCsv = [
      'Row Number,Email,Error Type,Error Message',
      ...importResult.errors.map(err => 
        `${err.rowNumber},"${err.email}","${err.errorType}","${err.errorMessage}"`
      )
    ].join('\n');

    const blob = new Blob([errorsCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-errors-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-import-modal-title"
        tabIndex={-1}
        className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col focus:outline-none"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
          <h2 id="user-import-modal-title" className="text-xl font-semibold text-secondary-900 dark:text-white">Import Users</h2>
          <button
            onClick={handleClose}
            className="text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {!importResult ? (
            <>
              {/* File Upload Area */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                  Upload File
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-secondary-300 dark:border-secondary-600 hover:border-secondary-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-primary-600" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="ml-4 text-secondary-500 dark:text-secondary-400 hover:text-secondary-700"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                      <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                        Drag and drop your file here, or click to browse
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Supports CSV and JSON files
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.json"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outline"
                        size="sm"
                        className="mt-4"
                      >
                        Select File
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Options */}
              <div className="mb-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                    File Format
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="csv"
                        checked={format === 'csv'}
                        onChange={(e) => setFormat(e.target.value as 'csv')}
                        className="mr-2"
                      />
                      <span className="text-sm text-secondary-700 dark:text-secondary-200">CSV</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="json"
                        checked={format === 'json'}
                        onChange={(e) => setFormat(e.target.value as 'json')}
                        className="mr-2"
                      />
                      <span className="text-sm text-secondary-700 dark:text-secondary-200">JSON</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={updateExisting}
                      onChange={(e) => setUpdateExisting(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-200">
                      Update existing users
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeMatchIdentifiers}
                      onChange={(e) => setIncludeMatchIdentifiers(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-200">
                      Include match identifiers
                    </span>
                  </label>
                </div>
              </div>

              {/* Template Download */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Need a template?
                    </p>
                    <p className="text-sm text-blue-700 mb-3">
                      Download a template file with the correct format and sample data
                    </p>
                    <Button
                      onClick={handleDownloadTemplate}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download {format.toUpperCase()} Template
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Import Results */
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">Total</p>
                  <p className="text-2xl font-semibold text-secondary-900 dark:text-white">
                    {importResult.summary.totalRecords}
                  </p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                  <p className="text-sm text-green-600 dark:text-green-400 mb-1">Created</p>
                  <p className="text-2xl font-semibold text-green-900">
                    {importResult.summary.successfulImports}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Updated</p>
                  <p className="text-2xl font-semibold text-blue-900">
                    {importResult.summary.updatedUsers}
                  </p>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-1">Errors</p>
                  <p className="text-2xl font-semibold text-red-900">
                    {importResult.summary.errorCount}
                  </p>
                </div>
              </div>

              {/* Success Message */}
              {importResult.summary.errorCount === 0 && (
                <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <p className="text-sm text-green-900">
                    All users imported successfully!
                  </p>
                </div>
              )}

              {/* Successful Imports */}
              {importResult.successfulImports.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-secondary-900 dark:text-white mb-3">
                    Successful Imports ({importResult.successfulImports.length})
                  </h3>
                  <div className="max-h-48 overflow-y-auto border border-secondary-200 dark:border-secondary-700 rounded-lg">
                    <table className="min-w-full divide-y divide-secondary-200">
                      <thead className="bg-secondary-50 dark:bg-secondary-900">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 dark:text-secondary-400">
                            Email
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 dark:text-secondary-400">
                            Status
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 dark:text-secondary-400">
                            Match IDs
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200">
                        {importResult.successfulImports.map((success, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm text-secondary-900 dark:text-white">
                              {success.email}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  success.status === 'CREATED'
                                    ? 'bg-green-100 dark:bg-green-900 text-green-800'
                                    : 'bg-blue-100 dark:bg-blue-900 text-blue-800'
                                }`}
                              >
                                {success.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-secondary-600 dark:text-secondary-400">
                              {success.matchIdentifiersAssigned}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {importResult.errors.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-secondary-900 dark:text-white">
                      Errors ({importResult.errors.length})
                    </h3>
                    <Button
                      onClick={handleDownloadErrors}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Errors
                    </Button>
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg">
                    <table className="min-w-full divide-y divide-red-200">
                      <thead className="bg-red-50 dark:bg-red-950">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400">
                            Row
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400">
                            Email
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-600 dark:text-red-400">
                            Error
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-secondary-800 divide-y divide-red-200">
                        {importResult.errors.map((error, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm text-secondary-900 dark:text-white">
                              {error.rowNumber}
                            </td>
                            <td className="px-4 py-2 text-sm text-secondary-900 dark:text-white">
                              {error.email}
                            </td>
                            <td className="px-4 py-2 text-sm text-red-600 dark:text-red-400">
                              {error.errorMessage}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 flex justify-end gap-3">
          {!importResult ? (
            <>
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={!selectedFile || importMutation.isPending}
              >
                {importMutation.isPending ? 'Importing...' : 'Import Users'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Close</Button>
          )}
        </div>
      </div>
    </div>
  );
}
